import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import AnnotationLayer from './AnnotationLayer'; // Import the AnnotationLayer

// Wrap component with forwardRef
const DesignCanvas = forwardRef(({
  html,
  annotations,
  onAnnotationStart, // Renamed prop for clarity
  onAnnotationChange, // Renamed prop for clarity
  onAnnotationComplete, // Renamed prop for clarity
  onAnnotationCancel, // Renamed prop for clarity
  deviceType = 'web', // Default to web
  activeTool = 'select', // New prop: 'select', 'pan', 'draw'
  zoomFactor = 1.2, // How much to zoom in/out with buttons
  selectedDrawings = [] // New prop for selected drawings
}, ref) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false); // For annotation/drawing tool

  // Keep track of previous device type to detect changes
  const prevDeviceTypeRef = useRef(deviceType);
  const containerRef = useRef(null); // Ref for the outer container (viewport)
  const contentRef = useRef(null); // Ref for the intermediate wrapper - RE-ADDED
  const deviceFrameRef = useRef(null); // Ref for the device frame
  const contentDivRef = useRef(null); // Ref for the actual content div where HTML is rendered

  // Reset zoom/pan when device type changes
  useEffect(() => {
    if (prevDeviceTypeRef.current !== deviceType) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
      if (containerRef.current) {
          containerRef.current.scrollTo(0, 0); // Reset scroll on device frame container
      }
      prevDeviceTypeRef.current = deviceType;
    }
  }, [deviceType]);

  // --- Zoom Functions ---
  const zoom = useCallback((direction) => {
      if (!containerRef.current) return;
      const newScaleFactor = direction === 'in' ? zoomFactor : 1 / zoomFactor;
      const newScale = Math.max(0.1, Math.min(scale * newScaleFactor, 5)); // Clamp scale

      // Zoom towards the center of the container
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      const pointX = (centerX - offset.x) / scale;
      const pointY = (centerY - offset.y) / scale;

      const newOffsetX = centerX - pointX * newScale;
      const newOffsetY = centerY - pointY * newScale;

      setScale(newScale);
      setOffset({ x: newOffsetX, y: newOffsetY });
  }, [scale, offset.x, offset.y, zoomFactor]);

  const zoomIn = useCallback(() => zoom('in'), [zoom]);
  const zoomOut = useCallback(() => zoom('out'), [zoom]);

  // --- Element identification function ---
  const getElementAtCoordinates = useCallback((x, y) => {
    if (!deviceFrameRef.current || !contentDivRef.current) return null;
    
    // Get all elements in the content div
    const elements = contentDivRef.current.querySelectorAll('*');
    
    // Find elements that contain the point
    const matchingElements = [];
    elements.forEach(element => {
      const rect = element.getBoundingClientRect();
      if (
        x >= rect.left && 
        x <= rect.right && 
        y >= rect.top && 
        y <= rect.bottom
      ) {
        matchingElements.push({
          element,
          area: rect.width * rect.height,
          depth: getElementDepth(element, contentDivRef.current),
          tagName: element.tagName.toLowerCase(),
          classes: element.className,
          id: element.id,
          textContent: element.textContent?.trim().substring(0, 50),
          rect: {
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
          }
        });
      }
    });
    
    // If we found elements, return the smallest/most specific one
    // Sort by area (ascending) and depth (descending)
    if (matchingElements.length > 0) {
      matchingElements.sort((a, b) => {
        // Prefer deeper elements
        if (b.depth !== a.depth) return b.depth - a.depth;
        // Then smaller area
        return a.area - b.area;
      });
      
      return matchingElements[0];
    }
    
    return null;
  }, []);
  
  // Helper function to get element depth in the DOM
  const getElementDepth = (element, root) => {
    let depth = 0;
    let current = element;
    
    while (current && current !== root && current !== document.body) {
      depth++;
      current = current.parentElement;
    }
    
    return depth;
  };
  
  // Get element description for a selection area
  const getElementsInSelectionArea = useCallback((selectionArea) => {
    if (!deviceFrameRef.current || !contentDivRef.current) return null;
    
    const elements = contentDivRef.current.querySelectorAll('*');
    const matchingElements = [];
    
    // Frame rect
    const frameRect = deviceFrameRef.current.getBoundingClientRect();
    
    // Calculate absolute coordinates of selection
    const absSelectionArea = {
      left: frameRect.left + selectionArea.x,
      top: frameRect.top + selectionArea.y,
      right: frameRect.left + selectionArea.x + selectionArea.width,
      bottom: frameRect.top + selectionArea.y + selectionArea.height
    };
    
    elements.forEach(element => {
      const rect = element.getBoundingClientRect();
      
      // Check for any overlap between element and selection
      const overlap = !(
        rect.right < absSelectionArea.left ||
        rect.left > absSelectionArea.right ||
        rect.bottom < absSelectionArea.top ||
        rect.top > absSelectionArea.bottom
      );
      
      if (overlap) {
        // Calculate overlap percentage
        const overlapWidth = Math.min(rect.right, absSelectionArea.right) - Math.max(rect.left, absSelectionArea.left);
        const overlapHeight = Math.min(rect.bottom, absSelectionArea.bottom) - Math.max(rect.top, absSelectionArea.top);
        const overlapArea = overlapWidth * overlapHeight;
        const elementArea = rect.width * rect.height;
        const overlapPercentage = overlapArea / elementArea;
        
        matchingElements.push({
          element,
          area: elementArea,
          depth: getElementDepth(element, contentDivRef.current),
          tagName: element.tagName.toLowerCase(),
          classes: element.className,
          id: element.id,
          textContent: element.textContent?.trim().substring(0, 50),
          overlapPercentage,
          rect: {
            left: rect.left - frameRect.left,
            top: rect.top - frameRect.top,
            width: rect.width,
            height: rect.height
          }
        });
      }
    });
    
    // Sort by overlap percentage (descending) and depth (descending)
    matchingElements.sort((a, b) => {
      // First prioritize overlap
      if (Math.abs(b.overlapPercentage - a.overlapPercentage) > 0.1) {
        return b.overlapPercentage - a.overlapPercentage;
      }
      // Then prioritize depth for similarly overlapped elements
      return b.depth - a.depth;
    });
    
    return matchingElements.slice(0, 5); // Return top 5 matching elements
  }, []);

  // Helper function to get drawing details (if needed by parent)
  const getDrawingDetails = useCallback(() => {
      // Return relevant drawing data, maybe just annotations classified as drawings
      return annotations.filter(ann => ann.points && ann.points.length > 0);
  }, [annotations]);

  // --- Selection Details Function ---
  // Combine logic to get details based on active tool
  const getSelectionDetails = useCallback(() => {
    if (activeTool === 'select') {
      if (annotations.length > 0 && annotations[0].width > 0 && annotations[0].height > 0) {
        const selectionArea = annotations[0]; // The rectangle
        return {
          selectionType: 'box',
          coordinates: selectionArea,
          selectedElements: getElementsInSelectionArea(selectionArea),
          drawings: [] // No drawings in box select mode
        };
      } else {
        return { selectionType: null, coordinates: null, selectedElements: [], drawings: [] }; // Nothing selected
      }
    } else if (activeTool === 'draw') {
      // Get all drawings
      const drawings = getDrawingDetails();
      // For now, just return the drawings themselves. Could add logic to find elements under drawings later.
      return {
        selectionType: 'drawings',
        coordinates: null, // No single box coordinate for drawings
        selectedElements: [], // Elements aren't directly selected by drawing yet
        drawings: drawings
      };
    } else {
      // Other tools don't have selection details
      return { selectionType: null, coordinates: null, selectedElements: [], drawings: [] };
    }
  }, [activeTool, annotations, getElementsInSelectionArea, getDrawingDetails]);

  // Expose zoom functions and element identification via ref
  useImperativeHandle(ref, () => ({
    zoomIn,
    zoomOut,
    getElementAtCoordinates,
    getElementsInSelectionArea,
    getSelectionDetails
  }));

  // Device-specific container classes (Frame styling)
  const deviceContainerClasses = {
    // Add min-height for empty web view to make border visible
    web: 'w-full h-full min-h-[600px]', 
    mobile: 'w-[393px] h-[852px] shadow-lg border border-gray-300 rounded-lg overflow-hidden bg-white dark:bg-gray-800 dark:border-gray-600',
    tablet: 'w-[768px] h-[1024px] shadow-lg border border-gray-300 rounded-lg overflow-hidden bg-white dark:bg-gray-800 dark:border-gray-600'
  };

  // --- Helper function to get adjusted coordinates for any device type ---
  const getAdjustedCoordinates = (clientX, clientY) => {
    // Check if the device frame reference exists
    if (!deviceFrameRef.current) return { x: clientX, y: clientY };

    // Get the bounding rectangle of the device frame element
    const frameRect = deviceFrameRef.current.getBoundingClientRect();

    // 1. Calculate pointer position relative to the device frame's current (transformed) top-left corner
    const xRelativeToTransformedFrame = clientX - frameRect.left;
    const yRelativeToTransformedFrame = clientY - frameRect.top;

    // 2. Adjust for the current scale factor to get coordinates relative to the unscaled frame origin
    const x = xRelativeToTransformedFrame / scale;
    const y = yRelativeToTransformedFrame / scale;

    // Return the adjusted coordinates
    return { x, y };
  };

  // --- Event Handlers based on Active Tool ---
  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left mouse button
      if (activeTool === 'pan') {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
        if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
      } else if (activeTool === 'draw' || activeTool === 'select') { // Treat select as annotation/draw for now
         e.preventDefault();
         setIsDrawing(true);
         if (onAnnotationStart) {
           const coords = getAdjustedCoordinates(e.clientX, e.clientY);
           const adjustedEvent = { ...e, clientX: coords.x, clientY: coords.y, currentTarget: deviceFrameRef.current };
           onAnnotationStart(adjustedEvent);
         }
      }
    }
    // Ignore other buttons (middle, right)
  };

  const handleMouseMove = (e) => {
    if (isPanning && activeTool === 'pan') {
      const newOffsetX = e.clientX - panStart.x;
      const newOffsetY = e.clientY - panStart.y;
      setOffset({ x: newOffsetX, y: newOffsetY });
    } else if (isDrawing && (activeTool === 'draw' || activeTool === 'select')) {
      if (onAnnotationChange) {
        const coords = getAdjustedCoordinates(e.clientX, e.clientY);
        const adjustedEvent = { ...e, clientX: coords.x, clientY: coords.y, currentTarget: deviceFrameRef.current };
        onAnnotationChange(adjustedEvent);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (e.button === 0) { // Left mouse button
        if (isPanning && activeTool === 'pan') {
          setIsPanning(false);
          if (containerRef.current) containerRef.current.style.cursor = 'grab'; // Or default cursor
        } else if (isDrawing && (activeTool === 'draw' || activeTool === 'select')) {
           setIsDrawing(false);
           if (onAnnotationComplete) {
             const coords = getAdjustedCoordinates(e.clientX, e.clientY);
             const adjustedEvent = { ...e, clientX: coords.x, clientY: coords.y, currentTarget: deviceFrameRef.current };
             onAnnotationComplete(adjustedEvent);
           }
        }
    }
  };

  const handleMouseLeave = (e) => {
    if (isPanning && activeTool === 'pan') {
      setIsPanning(false);
      if (containerRef.current) containerRef.current.style.cursor = 'grab';
    } else if (isDrawing && (activeTool === 'draw' || activeTool === 'select')) {
        // Decide if drawing should cancel or complete on mouse leave
        setIsDrawing(false);
        if (onAnnotationCancel) {
          const coords = getAdjustedCoordinates(e.clientX, e.clientY);
          const adjustedEvent = { ...e, clientX: coords.x, clientY: coords.y, currentTarget: deviceFrameRef.current };
          onAnnotationCancel(adjustedEvent);
        }
    }
  };

  // Touch event handlers for mobile/tablet
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      if (activeTool === 'pan') {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: touch.clientX - offset.x, y: touch.clientY - offset.y });
      } else if (activeTool === 'draw' || activeTool === 'select') {
        e.preventDefault();
        setIsDrawing(true);
        if (onAnnotationStart) {
          const coords = getAdjustedCoordinates(touch.clientX, touch.clientY);
          const adjustedEvent = { 
            ...e, 
            clientX: coords.x, 
            clientY: coords.y, 
            currentTarget: deviceFrameRef.current 
          };
          onAnnotationStart(adjustedEvent);
        }
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      if (isPanning && activeTool === 'pan') {
        const newOffsetX = touch.clientX - panStart.x;
        const newOffsetY = touch.clientY - panStart.y;
        setOffset({ x: newOffsetX, y: newOffsetY });
      } else if (isDrawing && (activeTool === 'draw' || activeTool === 'select')) {
        if (onAnnotationChange) {
          const coords = getAdjustedCoordinates(touch.clientX, touch.clientY);
          const adjustedEvent = { 
            ...e, 
            clientX: coords.x, 
            clientY: coords.y, 
            currentTarget: deviceFrameRef.current 
          };
          onAnnotationChange(adjustedEvent);
        }
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (isPanning && activeTool === 'pan') {
      setIsPanning(false);
    } else if (isDrawing && (activeTool === 'draw' || activeTool === 'select')) {
      setIsDrawing(false);
      if (onAnnotationComplete) {
        // Use the last known position for touch end
        const adjustedEvent = {
          ...e,
          currentTarget: deviceFrameRef.current
        };
        onAnnotationComplete(adjustedEvent);
      }
    }
  };

  // Determine cursor based on tool
  const getCursor = () => {
      switch(activeTool) {
          case 'pan': return isPanning ? 'grabbing' : 'grab';
          case 'draw': return 'crosshair';
          case 'select': return 'crosshair';
          default: return 'default';
      }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-auto cursor-default bg-gray-100 dark:bg-gray-900" 
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ cursor: getCursor() }}
    >
      {/* Intermediate Wrapper for Transform */}
      <div 
        ref={contentRef} 
        className="absolute top-0 left-0 flex items-center justify-center w-full h-full" // Use flex here to center the frame *within* the transformed area
        style={{
           transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
           transformOrigin: 'top left' // Keep origin at top-left for offset calculations
         }}
      >
        {/* Device Frame Container - remove transform from here */}
        <div
          ref={deviceFrameRef}
          className={`
            relative flex-shrink-0 // Keep flex-shrink-0
            ${deviceContainerClasses[deviceType] || deviceContainerClasses.web}
            ${!html && deviceType === 'web' ? 'border-2 border-dashed border-gray-300 dark:border-gray-600' : ''} 
          `}
          style={{
             // REMOVE transform styles from here
             transition: 'width 0.3s ease, height 0.3s ease',
          }}
        >
          {/* Content Div */}
          <div
            ref={contentDivRef}
            className={`w-full h-full ${deviceType !== 'web' ? 'bg-white dark:bg-gray-800' : ''}`} 
            dangerouslySetInnerHTML={{ __html: html || '' }}
          />
          {/* Annotation Layer */}
          <AnnotationLayer
            annotations={annotations}
            containerRef={deviceFrameRef}
            scale={scale}
            activeTool={activeTool}
            selectedDrawings={selectedDrawings}
          />
        </div>
      </div>
    </div>
  );
});

// Update AnnotationLayer to accept scale/offset if needed, or adjust coordinates here before passing

export { DesignCanvas };